<template>
  <div class="lsc-root">
    <!-- 期間切換 -->
    <div class="lsc-header">
      <div class="lsc-period">
        <button :class="['lsc-period-btn', period === 7 && 'active']" @click="$emit('period', 7)">7 天</button>
        <button :class="['lsc-period-btn', period === 30 && 'active']" @click="$emit('period', 30)">30 天</button>
      </div>
      <div class="lsc-legend">
        <span class="lsc-legend-dot" style="background:#818cf8;"></span><span>瀏覽</span>
        <span class="lsc-legend-dot" style="background:#34d399;"></span><span>轉換</span>
      </div>
    </div>

    <!-- 摘要數字 -->
    <div class="lsc-summary">
      <div class="lsc-stat">
        <div class="lsc-stat-val" style="color:#6366f1;">{{ stats.period_views?.toLocaleString() ?? 0 }}</div>
        <div class="lsc-stat-label">{{ period }} 天瀏覽</div>
      </div>
      <div class="lsc-stat">
        <div class="lsc-stat-val" style="color:#10b981;">{{ stats.period_conversions?.toLocaleString() ?? 0 }}</div>
        <div class="lsc-stat-label">{{ period }} 天轉換</div>
      </div>
      <div class="lsc-stat">
        <div class="lsc-stat-val" :style="{ color: convColor(stats.avg_conv_rate) }">
          {{ (stats.avg_conv_rate ?? 0).toFixed(1) }}%
        </div>
        <div class="lsc-stat-label">平均轉換率</div>
      </div>
      <div class="lsc-stat">
        <div class="lsc-stat-val" style="color:#64748b;">{{ stats.total_views?.toLocaleString() ?? 0 }}</div>
        <div class="lsc-stat-label">累計瀏覽</div>
      </div>
    </div>

    <!-- SVG 圖表 -->
    <div v-if="data.length" class="lsc-chart-wrap">
      <svg :viewBox="`0 0 ${W} ${H}`" class="lsc-svg">
        <!-- Grid lines -->
        <line v-for="(yv, yi) in yGridValues" :key="'g'+yi"
          :x1="PAD.l" :y1="yOf(yv)" :x2="W - PAD.r" :y2="yOf(yv)"
          stroke="#f1f5f9" stroke-width="1" />
        <!-- Y labels -->
        <text v-for="(yv, yi) in yGridValues" :key="'yl'+yi"
          :x="PAD.l - 4" :y="yOf(yv) + 3"
          text-anchor="end" :font-size="FONT" fill="#94a3b8">{{ fmtNum(yv) }}</text>

        <!-- Views bars -->
        <rect v-for="(d, i) in data" :key="'v'+i"
          :x="barX(i)" :y="yOf(d.views)"
          :width="barW * 0.55" :height="Math.max(1, plotH - yOf(d.views) + PAD.t)"
          fill="#818cf8" rx="2" opacity="0.85" />

        <!-- Conversions bars (overlaid, right-aligned in slot) -->
        <rect v-for="(d, i) in data" :key="'c'+i"
          :x="barX(i) + barW * 0.58" :y="yOf(d.conversions)"
          :width="barW * 0.38" :height="Math.max(1, plotH - yOf(d.conversions) + PAD.t)"
          fill="#34d399" rx="2" opacity="0.9" />

        <!-- Conversion rate line (right Y axis, scaled 0-100%) -->
        <polyline v-if="ratePoints"
          :points="ratePoints" fill="none"
          stroke="#f59e0b" stroke-width="1.5" stroke-linejoin="round"
          stroke-dasharray="4 2" opacity="0.8" />
        <circle v-for="(d, i) in data" :key="'r'+i"
          :cx="barX(i) + barW/2" :cy="rateY(d.conv_rate)"
          r="2.5" fill="#f59e0b" opacity="0.9" />

        <!-- X labels -->
        <text v-for="(d, i) in xLabelData" :key="'xl'+i"
          :x="barX(i) + barW/2" :y="H - PAD.b + 12"
          text-anchor="middle" :font-size="FONT" fill="#94a3b8">
          {{ d.label }}
        </text>

        <!-- Right axis labels (conversion rate) -->
        <text v-for="rv in [0, 50, 100]" :key="'rl'+rv"
          :x="W - PAD.r + 3" :y="rateY(rv) + 3"
          text-anchor="start" :font-size="FONT" fill="#d97706" opacity="0.8">
          {{ rv }}%
        </text>

        <!-- "無資料" message -->
        <text v-if="allZero"
          :x="W/2" :y="H/2"
          text-anchor="middle" font-size="12" fill="#cbd5e1">
          此期間尚無瀏覽資料
        </text>
      </svg>

      <!-- Conv rate legend -->
      <div class="lsc-rate-legend">
        <span class="lsc-legend-dot" style="background:#f59e0b;width:14px;height:2px;border-radius:0;display:inline-block;vertical-align:middle;margin-right:4px;"></span>
        <span style="font-size:.7rem;color:#b45309;">轉換率（右軸）</span>
      </div>
    </div>

    <div v-else class="lsc-empty">載入中…</div>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  stats: { type: Object, default: () => ({}) },
  period: { type: Number, default: 7 }
})
defineEmits(['period'])

const data = computed(() => props.stats.stats || [])
const allZero = computed(() => data.value.every(d => d.views === 0 && d.conversions === 0))

// Chart dimensions
const W = 500, H = 180
const PAD = { t: 16, r: 36, b: 28, l: 38 }
const FONT = 8
const plotW = computed(() => W - PAD.l - PAD.r)
const plotH = H - PAD.t - PAD.b

const maxViews = computed(() => Math.max(...data.value.map(d => d.views), 1))
const barSlotW = computed(() => plotW.value / (data.value.length || 1))
const barW = computed(() => barSlotW.value * 0.9)

const barX = (i) => PAD.l + i * barSlotW.value + barSlotW.value * 0.05

const yOf = (v) => PAD.t + plotH * (1 - v / maxViews.value)
const rateY = (pct) => PAD.t + plotH * (1 - pct / 100)

const yGridValues = computed(() => {
  const max = maxViews.value
  if (max <= 5) return [0, Math.ceil(max / 2), max]
  const step = Math.ceil(max / 4 / 10) * 10 || 1
  return [0, step, step * 2, step * 3, step * 4].filter(v => v <= max + step)
})

const fmtNum = (v) => v >= 1000 ? (v / 1000).toFixed(1) + 'k' : String(v)

const ratePoints = computed(() => {
  if (!data.value.length) return null
  return data.value.map((d, i) =>
    `${barX(i) + barW.value / 2},${rateY(d.conv_rate)}`
  ).join(' ')
})

// Only show X labels for a subset to avoid crowding
const xLabelData = computed(() => {
  const n = data.value.length
  if (n <= 7) return data.value.map((d, i) => ({ ...d, label: fmtDate(d.date) }))
  const step = Math.ceil(n / 7)
  return data.value.map((d, i) => ({ ...d, label: i % step === 0 ? fmtDate(d.date) : '' }))
})

const fmtDate = (ds) => {
  if (!ds) return ''
  const [, m, d] = ds.split('-')
  return `${+m}/${+d}`
}

const convColor = (r) => r >= 5 ? '#10b981' : r >= 2 ? '#f59e0b' : '#94a3b8'
</script>

<style scoped>
.lsc-root { display: flex; flex-direction: column; gap: 12px; }
.lsc-header { display: flex; align-items: center; justify-content: space-between; }
.lsc-period { display: flex; gap: 4px; background: #f1f5f9; border-radius: 7px; padding: 3px; }
.lsc-period-btn {
  padding: 4px 12px; border: none; border-radius: 5px; font-size: .75rem;
  background: none; color: #64748b; cursor: pointer; font-weight: 500;
  transition: background .12s, color .12s;
}
.lsc-period-btn.active { background: #fff; color: #4f46e5; box-shadow: 0 1px 4px rgba(0,0,0,.08); }
.lsc-legend { display: flex; align-items: center; gap: 8px; font-size: .72rem; color: #64748b; }
.lsc-legend-dot { width: 10px; height: 10px; border-radius: 50%; display: inline-block; }

.lsc-summary { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; }
.lsc-stat { background: #f8fafc; border-radius: 8px; padding: 10px 12px; text-align: center; }
.lsc-stat-val { font-size: 1.25rem; font-weight: 700; line-height: 1.2; }
.lsc-stat-label { font-size: .7rem; color: #94a3b8; margin-top: 2px; }

.lsc-chart-wrap { position: relative; }
.lsc-svg { width: 100%; height: auto; display: block; }
.lsc-rate-legend { text-align: right; margin-top: 2px; }
.lsc-empty { text-align: center; padding: 24px; color: #cbd5e1; font-size: .85rem; }
</style>
